import { createApi } from '@reduxjs/toolkit/query/react';
import moment from 'moment';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';
import { CommercialOfferModel } from '../models/CommercialOffer';
import { ordersApi } from 'api/orders';

interface LeadTime {
  min: number;
  max: number;
  timeUnit: string;
}

export interface CommercialOffer {
  id: string;
  uuid: string | number;
  createDate: string;
  lastModifiedDate: string;
  offerNumber: string;
  status: string;
  clientId: string;
  reason?: string;
  delivery: Record<string, any>;
  brands: Record<string, any>[];
  paymentTerms: Record<string, any>;
  additionalInformation?: string;
  itemsPrice: number;
  deliveryCost: number;
  additionalFees: number;
  total: number;
  leadTime: LeadTime;
  substitute: boolean;
  alternative: boolean;
  formattedLastModifiedDate?: string;
  formattedCreateDate?: string;
  formattedValidityDate?: string;
  leadTimeText?: string;
  totalMin: string;
  totalMax: string;
  validityDate: string;
  isFinalStatus: boolean;
}

export const commercialOffersApi = createApi({
  tagTypes: ['COMMERCIAL_OFFER', 'COMMERCIAL_OFFER_LIST'],
  reducerPath: 'commercialOffersApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    commercialOfferList: builder.query<CommercialOfferModel[], string>({
      query: (dealId) => ({
        url: `/wrd/api/commercial-offer?dealId=${dealId}`,
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data.map((offer: any) => ({
          ...offer,
          formattedLastModifiedDate: offer.lastModifiedDate ? moment(offer.lastModifiedDate).format('DD.MM.YYYY') : '',
          formattedCreateDate: offer.createDate ? moment(offer.createDate).format('DD.MM.YYYY') : '',
          formattedValidityDate: offer.validityDate ? moment(offer.validityDate).format('DD.MM.YYYY') : '',
          leadTimeText: offer.leadTime
            ? `${offer.leadTime.min}-${offer.leadTime.max} ${offer.leadTime.timeUnit.toLowerCase()}s`
            : '',
        }));
      },
      providesTags: () => ['COMMERCIAL_OFFER_LIST'],
    }),
    commercialOfferById: builder.query<CommercialOffer, { id: string }>({
      query: ({ id }) => ({
        url: `/wrd/api/commercial-offer/${id}`,
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        const offerData = response.data || {};
        return {
          ...offerData,
          formattedLastModifiedDate: offerData.lastModifiedDate
            ? moment(offerData.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss')
            : '',
          formattedCreateDate: offerData.createDate ? moment(offerData.createDate).format('DD.MM.YYYY HH:mm:ss') : '',
          formattedValidityDate: offerData.validityDate ? moment(offerData.validityDate).format('DD.MM.YYYY') : '',
          leadTimeText: offerData.leadTime
            ? `${offerData.leadTime.min}-${offerData.leadTime.max} ${offerData.leadTime.timeUnit.toLowerCase()}s`
            : '',
          isFinalStatus:
            offerData?.status === 'REJECTED' ||
            offerData?.status === 'PRICE_CONFIRMATION_REQUESTED' ||
            offerData?.status === 'PRE_INVOICE_SENT' ||
            offerData?.status === 'ORDER_CONFIRMATION_SENT',
        };
      },
      providesTags: () => ['COMMERCIAL_OFFER'],
    }),
    createCommercialOffer: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/commercial-offer',
        method: 'post',
        body: data,
      }),
      invalidatesTags: () => ['COMMERCIAL_OFFER_LIST'],
    }),
    updateCommercialOffer: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/commercial-offer',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['COMMERCIAL_OFFER', 'COMMERCIAL_OFFER_LIST'],
    }),
    deleteCommercialOffer: builder.mutation<any, string>({
      query: (id) => ({
        url: `/wrd/api/commercial-offer/${id}`,
        method: 'delete',
      }),
      invalidatesTags: () => ['COMMERCIAL_OFFER_LIST'],
    }),
    rejectCommercialOffer: builder.mutation<any, any>({
      query: ({ data, commercialOfferId }) => ({
        url: `/wrd/api/commercial-offer/reject/${commercialOfferId}`,
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['COMMERCIAL_OFFER', 'COMMERCIAL_OFFER_LIST'],
    }),
    negotiateCommercialOffer: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/commercial-offer/negotiate',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['COMMERCIAL_OFFER', 'COMMERCIAL_OFFER_LIST'],
    }),
    sendOffer: builder.mutation<any, any>({
      query: ({ data }) => ({
        url: `/wrd/api/request-price/commercial-offer`,
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(ordersApi.util.invalidateTags(['ORDER_LIST']));
      },
      invalidatesTags: () => ['COMMERCIAL_OFFER', 'COMMERCIAL_OFFER_LIST'],
    }),
    commercialOfferDeleteItem: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/commercial-offer/delete-item',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['COMMERCIAL_OFFER', 'COMMERCIAL_OFFER_LIST'],
    }),
    commercialOfferDeleteItemAttachment: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/commercial-offer/delete-item-attachment',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['COMMERCIAL_OFFER', 'COMMERCIAL_OFFER_LIST'],
    }),

    purchaseRequestBrandSelectorList: builder.query<any, { dealId: string }>({
      query: (args) => {
        const { dealId } = args;
        return {
          url: `/wrd/api/purchase-request/deal/${dealId}/selector-list`,
        };
      },
      transformResponse: (response: any) => {
        // Ensure consistent data structure
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        return response.data;
      },
      // providesTags: () => ['DEAL_COMMERCIAL_OFFER'],
    }),
    supplierItemSelectorList: builder.query<any, { purchaseRequestId: string }>({
      query: (args) => {
        const { purchaseRequestId } = args;
        return {
          url: `/wrd/api/global-supplier-offer/selector-list/${purchaseRequestId}`,
        };
      },
      transformResponse: (response: any) => {
        // Ensure consistent data structure
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.supplierName,
          value: item.supplierOfferId,
        }));
        return response.data;
      },
      // providesTags: () => ['DEAL_COMMERCIAL_OFFER'],
    }),
    generateCommercialOffer: builder.mutation<any, { commercialOfferId: string }>({
      query: ({ commercialOfferId }) => ({
        url: `/wrd/api/commercial-offer/generate-commercial-offer/${commercialOfferId}`,
        method: 'post',
      }),
      invalidatesTags: () => ['COMMERCIAL_OFFER', 'COMMERCIAL_OFFER_LIST'],
    }),
    copyCommercialOffer: builder.mutation<any, { commercialOfferId: string }>({
      query: ({ commercialOfferId }) => ({
        url: `/wrd/api/commercial-offer/copy/${commercialOfferId}`,
        method: 'post',
      }),
      invalidatesTags: () => ['COMMERCIAL_OFFER', 'COMMERCIAL_OFFER_LIST'],
    }),
    requestPriceConfirmation: builder.mutation<any, { commercialOfferId: string }>({
      query: ({ commercialOfferId }) => ({
        url: `/wrd/api/commercial-offer/request-price-confirmation/${commercialOfferId}`,
        method: 'put',
      }),
      invalidatesTags: () => ['COMMERCIAL_OFFER', 'COMMERCIAL_OFFER_LIST'],
    }),
    commercialOfferGetById: builder.query<any, { commercialOfferId: string }>({
      query: ({ commercialOfferId }) => ({
        url: `/wrd/api/commercial-offer/${commercialOfferId}`,
        method: 'get',
      }),
      providesTags: () => ['COMMERCIAL_OFFER'],
    }),
    commercialOfferCustomShippingAddressUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/shipping-address/custom',
        method: 'put',
        body: data,
      }),
    }),
    commercialOfferCustomShippingAddressCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/shipping-address/custom',
        method: 'post',
        body: data,
      }),
    }),
  }),
});

export const {
  useCommercialOfferListQuery,
  useCommercialOfferByIdQuery,
  useCreateCommercialOfferMutation,
  useUpdateCommercialOfferMutation,
  useDeleteCommercialOfferMutation,
  useRejectCommercialOfferMutation,
  useNegotiateCommercialOfferMutation,
  useSendOfferMutation,
  usePurchaseRequestBrandSelectorListQuery,
  useSupplierItemSelectorListQuery,
  useGenerateCommercialOfferMutation,
  useCopyCommercialOfferMutation,
  useCommercialOfferGetByIdQuery,
  useRequestPriceConfirmationMutation,
  useCommercialOfferDeleteItemMutation,
  useCommercialOfferDeleteItemAttachmentMutation,
  useCommercialOfferCustomShippingAddressCreateMutation,
  useCommercialOfferCustomShippingAddressUpdateMutation,
} = commercialOffersApi;
